import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { Empty } from '../components/Shell.jsx';

const ROLE_LABEL = {
  practice_admin: 'Administrator',
  practice_staff: 'Bookkeeper',
  read_only: 'Read only',
  client_admin: 'Client admin',
  client_user: 'Client user',
};

const ROLE_HELP = {
  practice_admin: 'Everything, including managing people and closing periods.',
  practice_staff: 'Posts transactions and runs reports.',
  read_only: 'Runs reports. Posts nothing.',
  client_admin: 'Raises invoices, captures slips, and sees how the business is doing.',
  client_user: 'Captures slips. Sees no figures.',
};

/** People in the practice, and who may see which client. */
export function People({ entities, currentUserId, isPracticeUser, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [link, setLink] = useState(null);
  const [invite, setInvite] = useState({ email: '', fullName: '', roles: {} });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/api/users').then(setData).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);

  const sendInvite = async () => {
    setBusy(true); setErr(null); setMsg(null); setLink(null);
    try {
      const roles = Object.entries(invite.roles)
        .filter(([, role]) => role)
        .map(([entityId, role]) => ({ entityId, role }));
      const r = await api.post('/api/users/invite', { ...invite, roles });

      if (r.emailSent) {
        setMsg(`Invitation emailed to ${r.invitation.email}.`);
      } else {
        // Mail being down must not stop an admin adding a colleague.
        setMsg(`Invitation created for ${r.invitation.email}, but the email could not be sent.`);
        setLink(r.url);
      }
      setInvite({ email: '', fullName: '', roles: {} });
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const saveRoles = async (userId, roles) => {
    setErr(null); setMsg(null);
    try {
      await api.put(`/api/users/${userId}/roles`, { roles });
      setEditing(null); setMsg('Access updated.'); load(); onChanged?.();
    } catch (e) { setErr(e.message); }
  };

  const setActive = async (userId, isActive) => {
    setErr(null);
    try { await api.post(`/api/users/${userId}/active`, { isActive }); load(); }
    catch (e) { setErr(e.message); }
  };

  const revoke = async (id) => {
    setErr(null);
    try { await api.post(`/api/users/invitations/${id}/revoke`); load(); }
    catch (e) { setErr(e.message); }
  };

  if (!data) return <div className="empty">Loading…</div>;

  const inviteReady = invite.email && invite.fullName.length > 1
    && Object.values(invite.roles).some(Boolean);

  return (
    <>
      <header className="rhead">
        <h1 className="rhead__title display">People</h1>
        <div className="rhead__sub">
          {isPracticeUser
            ? 'Who works in this practice, and which client companies each of them can see.'
            : 'Who in your company can use Ledger, and what they can do.'}
        </div>
      </header>

      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      {link && (
        <div className="note note--warn">
          <strong>Send this link to them yourself.</strong> It works once and expires in 72 hours.
          <div style={{ marginTop: 8 }}>
            <input readOnly value={link} onFocus={(e) => e.target.select()}
                   style={{ width: '100%', fontFamily: 'var(--figure)', fontSize: 12 }} />
          </div>
        </div>
      )}

      <h2 className="eyebrow" style={{ margin: '26px 0 10px' }}>Invite someone</h2>
      <div className="form-grid">
        <label className="field"><span>Full name</span>
          <input value={invite.fullName} onChange={(e) => setInvite({ ...invite, fullName: e.target.value })} /></label>
        <label className="field"><span>Email</span>
          <input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></label>
      </div>

      <div className="scroll-x">
        <table className="ledger">
          <thead><tr><th>{isPracticeUser ? 'Client company' : 'Company'}</th>
                     <th style={{ width: 260 }}>Access</th></tr></thead>
          <tbody>
            {entities.map((e) => (
              <tr key={e.id}>
                <td>{e.name}</td>
                <td>
                  <select value={invite.roles[e.id] ?? ''} style={{ width: '100%' }}
                          onChange={(ev) => setInvite({
                            ...invite, roles: { ...invite.roles, [e.id]: ev.target.value } })}>
                    <option value="">No access</option>
                    {(data?.grantableRoles ?? []).map((r) =>
                      <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="note">
        {(data?.grantableRoles ?? []).filter((r) => ROLE_HELP[r]).map((r) =>
          <div key={r}><strong>{ROLE_LABEL[r]}</strong> — {ROLE_HELP[r]}</div>)}
      </div>

      <button className="btn" style={{ marginTop: 14 }} onClick={sendInvite} disabled={!inviteReady || busy}>
        {busy ? 'Sending' : 'Send invitation'}
      </button>
      {!data.mailConfigured && (
        <div className="note">
          Email is not configured on this server, so you will be given a link to pass on yourself.
        </div>
      )}

      <h2 className="eyebrow" style={{ margin: '34px 0 10px' }}>
        {isPracticeUser ? 'Practice members' : 'Your team'}
      </h2>
      <div className="scroll-x">
        <table className="ledger">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Access</th><th>Last signed in</th><th style={{ width: 170 }} /></tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.id}>
                <td>
                  {u.full_name}
                  {u.id === currentUserId && <span style={{ color: 'var(--ink-faint)' }}> · you</span>}
                  {!u.is_active && <span className="neg"> · deactivated</span>}
                </td>
                <td className="figure" style={{ fontSize: 12 }}>{u.email}</td>
                <td>
                  {editing === u.id ? (
                    <RoleEditor entities={entities} current={u.roles}
                                grantable={data.grantableRoles}
                                onCancel={() => setEditing(null)}
                                onSave={(roles) => saveRoles(u.id, roles)} />
                  ) : (
                    u.roles.length === 0
                      ? <span style={{ color: 'var(--ink-faint)' }}>No client access</span>
                      : u.roles.map((r) => (
                          <div key={r.entityId} style={{ fontSize: 12 }}>
                            {r.entityName} — <span style={{ color: 'var(--ink-soft)' }}>{ROLE_LABEL[r.role]}</span>
                          </div>
                        ))
                  )}
                </td>
                <td className="figure" style={{ fontSize: 12 }}>
                  {u.last_login_at ? u.last_login_at.slice(0, 10) : 'never'}
                </td>
                <td className="num">
                  {editing !== u.id && (
                    <>
                      <button className="icon-btn" onClick={() => setEditing(u.id)}>Access</button>{' '}
                      {u.id !== currentUserId && (
                        <button className="icon-btn" onClick={() => setActive(u.id, !u.is_active)}>
                          {u.is_active ? 'Deactivate' : 'Restore'}
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.invites.length > 0 && (
        <>
          <h2 className="eyebrow" style={{ margin: '34px 0 10px' }}>Invitations not yet accepted</h2>
          <div className="scroll-x">
            <table className="ledger">
              <thead><tr><th>Name</th><th>Email</th><th>Expires</th><th style={{ width: 100 }} /></tr></thead>
              <tbody>
                {data.invites.map((i) => (
                  <tr key={i.id}>
                    <td>{i.full_name}</td>
                    <td className="figure" style={{ fontSize: 12 }}>{i.email}</td>
                    <td className="figure" style={{ fontSize: 12 }}>{i.expires_at.slice(0, 16).replace('T', ' ')}</td>
                    <td className="num"><button className="icon-btn" onClick={() => revoke(i.id)}>Revoke</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

function RoleEditor({ entities, current, grantable, onSave, onCancel }) {
  const [roles, setRoles] = useState(
    Object.fromEntries(current.map((r) => [r.entityId, r.role])));

  return (
    <div>
      {entities.map((e) => (
        <div key={e.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontSize: 12, flex: 1 }}>{e.name}</span>
          <select value={roles[e.id] ?? ''} style={{ fontSize: 12, padding: '3px 6px' }}
                  onChange={(ev) => setRoles({ ...roles, [e.id]: ev.target.value })}>
            <option value="">No access</option>
            {(grantable ?? []).map((r) =>
              <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </select>
        </div>
      ))}
      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
        <button className="btn" style={{ padding: '5px 12px', fontSize: 11 }}
                onClick={() => onSave(Object.entries(roles).filter(([, r]) => r)
                  .map(([entityId, role]) => ({ entityId, role })))}>Save</button>
        <button className="btn btn--quiet" style={{ padding: '5px 12px', fontSize: 11 }}
                onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
